import { buildPharmacyCategoryPath } from '@cannect/new-components/organisms/PillPharmacySection/constants'
import { PROGRAM_HERO_SLUG } from '../constants'
import {
  useCreateScreeningSession,
  useScreeningAnswer,
  useScreeningBack,
  useScreeningContact,
  useScreeningDecline,
  useScreeningNext,
  useBindScreeningCpf,
  useCreateProgramOrder
} from '@cannect/services/resources/screening/hooks'
import { useCreateAppointment } from '@cannect/services/resources/scheduling/hooks'
import type { TScreeningNext, TScreeningQuestion } from '@cannect/services/resources/screening/types'
import { useTokenApi } from '@cannect/hooks/useTokenApi'
import { setUser } from 'hooks/useSessionUser'
import useSignedUser from 'hooks/useSignedUser'
import { defaultsHeadersAxios } from 'services/api'
import { clearMask, dateMask, formatCpf, formatPhone } from 'utils/formatters'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { sendTokenForgotPassword } from 'services/user'
import { useEffect, useMemo, useRef, useState } from 'react'
import { TrailCta, TrailGhost, TrailShell } from './TrailShell'
import {
  CPF_ERROR,
  EMAIL_ERROR,
  PHONE_ERROR,
  isValidBirthdayMask,
  isValidCpf,
  isValidEmail,
  isValidPhone
} from './validation'
import {
  CLINICAL_PROGRESS_SPAN,
  CLINICAL_PROGRESS_START,
  CLINICAL_TEXT_PLACEHOLDERS,
  CLINICAL_TOPICS,
  MY_APPOINTMENTS_PATH,
  WHATSAPP_PROGRAM_DOUBTS_URL
} from './copy'
import { ScheduleScreen } from './ScheduleScreen'
import { TERMS_VERSION } from './terms'
import { ProgramCartFlow } from './ProgramCartFlow'
import {
  BindScreen,
  ChoiceScreen,
  ClinicalIntroScreen,
  ConfirmDataScreen,
  type TConfirmData,
  DeclineScreen,
  DoneFooter,
  DoneScreen,
  ExitFooter,
  ExitScreen,
  ImcResultScreen,
  ScheduledScreen,
  ClinicalMultiScreen,
  ClinicalTextScreen,
  MeasuresScreen,
  OfferFooter,
  PaymentConfirmedScreen,
  PaymentConfirmingScreen,
  PaymentFailedScreen,
  OfferScreen,
  OptionsScreen,
  SafetyScreen,
  StartContactScreen,
  TextCollectScreen,
  type TProgramPaymentMethod,
  YnDetailScreen
} from './screens'
import {
  buildTrailPath,
  legacyTrailLocation,
  persistScheduledLabel,
  persistScreeningToken,
  rememberTrailSearch,
  storedScheduledLabel,
  trailStepFromSearch,
  type TTrailStep
} from './paths'
import {
  attachProgramOrderIfNeeded,
  hasPaidProgramPayment,
  readProgramPaymentSummary,
  saveProgramPaymentSummary
} from './programCart'
import {
  getScreeningNext,
  postScreeningContact,
  postScreeningProgramOrder
} from '@cannect/services/resources/screening/requests'
import { useMutation, useQueryClient } from '@tanstack/react-query'

const apiMessage = (error: unknown, fallback: string) =>
  (error as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback

const parseImcValue = (value: unknown) => {
  if (!value || typeof value !== 'object') return null
  const data = value as { peso?: number; altura?: number; imc?: number; meta?: number; peso_maximo?: number }
  if (!Number.isFinite(Number(data.imc))) return null
  return {
    peso: Number(data.peso),
    altura: Number(data.altura),
    imc: Number(data.imc),
    meta: Number.isFinite(Number(data.meta)) ? Number(data.meta) : undefined,
    pesoMaximo: Number.isFinite(Number(data.peso_maximo)) ? Number(data.peso_maximo) : undefined
  }
}

const stepIndexFor = (step: TTrailStep | '', overlay?: string) => {
  if (overlay === 'imc') return 5
  if (overlay === 'decline') return 8
  if (overlay === 'intro') return 18
  if (overlay === 'confirm') return 16
  if (overlay === 'scheduled') return 17
  const map: Record<string, number> = {
    escolha: 1,
    nome: 2,
    whatsapp: 2,
    nascimento: 3,
    menor: 3,
    medidas: 4,
    imc: 5,
    'imc-fora': 5,
    seguranca: 6,
    perfil: 7,
    oferta: 8,
    'agora-nao': 8,
    identificacao: 9,
    pagamento: 12,
    'pagamento-erro': 12,
    pago: 13,
    agenda: 15,
    dados: 16,
    perguntas: 18,
    clinica: 19,
    enviado: 19
  }
  return map[step] || 1
}

const stepFromNext = (next: TScreeningNext | undefined, overlay?: string): TTrailStep => {
  if (overlay === 'imc') return next?.exit_reason === 'imc' ? 'imc-fora' : 'imc'
  if (overlay === 'decline') return 'agora-nao'
  if (overlay === 'intro') return 'perguntas'
  if (overlay === 'confirm' || overlay === 'scheduled') return 'dados'
  if (!next) return 'escolha'
  if (next.phase === 'who') return 'nome'
  if (next.phase === 'contact') {
    if (next.question?.key === 'patient_name') return 'nome'
    if (
      next.question?.key === 'contact_phone' ||
      next.question?.key === 'contact_email' ||
      next.question?.type === 'phone'
    )
      return 'whatsapp'
    return 'nome'
  }
  if (next.phase === 'birthday') return 'nascimento'
  if (next.phase === 'ineligible' && next.exit_reason === 'underage') return 'menor'
  if (next.phase === 'ineligible' && next.exit_reason === 'imc') return 'imc-fora'
  if (next.phase === 'ineligible') return 'perfil'
  if (next.phase === 'eligibility' && next.question?.key === 'imc') return 'medidas'
  if (next.phase === 'eligibility') return 'seguranca'
  if (next.phase === 'offer' || next.phase === 'awaiting_payment')
    return next.phase === 'awaiting_payment' ? 'identificacao' : 'oferta'
  if (next.phase === 'awaiting_scheduling') return 'agenda'
  if (next.phase === 'clinical') return 'clinica'
  if (next.phase === 'done' || next.phase === 'declined') return 'enviado'
  return 'escolha'
}

// slot.label chega como "Consulta médica, ter, 20 de outubro, 14:00"
const maskConfirmField = (field: keyof TConfirmData, value: string) => {
  if (field === 'birthday') return dateMask(value)
  if (field === 'cpf') return formatCpf(value)
  return value
}

const isCollectValid = (type: string, value: string) => {
  if (type === 'phone') return isValidPhone(value)
  if (type === 'email') return isValidEmail(value)
  if (type === 'date') return isValidBirthdayMask(dateMask(value))
  return Boolean(value.trim())
}

const scheduledPhrase = (label: string) => {
  const parts = label.split(', ').slice(1)
  const time = parts.pop() || ''
  const text = `${parts.join(', ')}, às ${time}`
  return text.charAt(0).toUpperCase() + text.slice(1)
}

// "Consulta médica, ter, 20 de outubro, 14:00" -> "ter, 20 de outubro, às 14:00"
const scheduledAppointment = (label: string) => {
  const parts = label.split(', ').slice(1)
  const time = parts.pop() || ''
  return `${parts.join(', ')}, às ${time}`
}

export const RedirectLegacyProgramTrail = () => {
  const { step } = useParams<{ step?: string }>()
  const { search } = useLocation()
  const history = useHistory()

  useEffect(() => {
    const next = legacyTrailLocation(step, search)
    // usePreserveUTM also replace()s on mount; a sync Redirect lost that race and left /triagem/:step blank.
    const id = window.setTimeout(() => history.replace(next), 0)
    return () => window.clearTimeout(id)
  }, [history, search, step])

  return null
}

export const WeightLossProgramTrail = ({ onClose }: { onClose: () => void }) => {
  const history = useHistory()
  const location = useLocation()
  const { search } = location
  const routeStep = trailStepFromSearch(search)
  const queryToken = new URLSearchParams(search).get('session') || ''
  const [token, setToken] = useState(queryToken)
  const [choice, setChoice] = useState('journey')
  const [startName, setStartName] = useState('')
  const [typedBirthday, setTypedBirthday] = useState('')
  const [payMethod, setPayMethod] = useState<TProgramPaymentMethod>('pix')
  // Depois do CPF a sessão sai de "oferta"; este estado deixa o voltar rever a oferta sem reabrir a triagem.
  const [reviewOffer, setReviewOffer] = useState(false)
  const [startPhone, setStartPhone] = useState('')
  const [startEmail, setStartEmail] = useState('')
  const [consent, setConsent] = useState(false)
  const [overlay, setOverlay] = useState<'imc' | 'decline' | 'intro' | 'confirm' | 'scheduled' | null>(null)
  const [imcSnapshot, setImcSnapshot] = useState<ReturnType<typeof parseImcValue>>(null)
  const imcSnapshotRef = useRef(imcSnapshot)
  imcSnapshotRef.current = imcSnapshot
  const [collectValue, setCollectValue] = useState('')
  const [height, setHeight] = useState('')
  const [weight, setWeight] = useState('')
  const [maxWeight, setMaxWeight] = useState('')
  const [meta, setMeta] = useState('')
  const [safety, setSafety] = useState<string[]>([])
  const [decline, setDecline] = useState<string[]>([])
  const [cpf, setCpf] = useState('')
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [optionPick, setOptionPick] = useState('')
  const [ynValue, setYnValue] = useState<number | null>(null)
  const [ynDetail, setYnDetail] = useState('')
  const [multiPick, setMultiPick] = useState<string[]>([])
  const [confirmEdits, setConfirmEdits] = useState<Partial<TConfirmData>>({})
  const [slot, setSlot] = useState<{
    prescriberId: number
    prescriberUserId?: number
    duration?: number
    name: string
    date: string
    hour: string
    label: string
  } | null>(null)
  const [introSeen, setIntroSeen] = useState(false)

  const { data: next, isLoading, isError } = useScreeningNext(token || undefined)
  const createSession = useCreateScreeningSession()
  const contact = useScreeningContact(token)
  const answer = useScreeningAnswer(token)
  const back = useScreeningBack(token)
  const declineMutation = useScreeningDecline(token)
  const bindCpf = useBindScreeningCpf()
  const createProgramOrder = useCreateProgramOrder()
  const createAppointment = useCreateAppointment()
  const { setTokenApi } = useTokenApi()
  const { signedUser } = useSignedUser()
  const queryClient = useQueryClient()
  const passwordEmail = useMutation({ mutationFn: (email: string) => sendTokenForgotPassword({ email }) })
  const [passwordEmailSent, setPasswordEmailSent] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)

  useEffect(() => {
    rememberTrailSearch(search)
  }, [search])

  // Quem abre a trilha por um link (ou recarrega em outra aba) ainda não passou pelo passo de dados que gravava o token;
  // o vínculo do pedido pago e o retorno do pagamento leem o token daqui.
  useEffect(() => {
    if (token) persistScreeningToken(token)
  }, [token])

  useEffect(() => {
    if (queryToken && queryToken !== token) setToken(queryToken)
  }, [queryToken, token])

  useEffect(() => {
    if (next?.contact?.patient_name && !startName) setStartName(String(next.contact.patient_name))
    if (next?.contact?.contact_phone && !startPhone) setStartPhone(formatPhone(String(next.contact.contact_phone)))
    if (next?.contact?.contact_email && !startEmail) setStartEmail(String(next.contact.contact_email))
  }, [
    next?.contact?.patient_name,
    next?.contact?.contact_phone,
    next?.contact?.contact_email,
    startName,
    startPhone,
    startEmail
  ])

  const currentStep = useMemo(() => stepFromNext(next, overlay || undefined), [next, overlay])

  // Pago na tela de pagamento, mas a sessão ainda aguarda o vínculo do pedido: tenta vincular até a API reconhecer.
  // Conta criada pela triagem: a pessoa ainda não tem senha. No fim da jornada o e-mail de redefinição de senha (o fluxo
  // normal de "esqueci minha senha") é enviado uma única vez por sessão, e a tela explica o próximo passo.
  const newAccountEmail =
    next?.phase === 'done' && next.contact?.account_created ? next.contact.contact_email || '' : ''

  const sendPasswordEmail = () => {
    if (!newAccountEmail) return
    passwordEmail.mutate(newAccountEmail, {
      onSuccess: () => {
        setPasswordEmailSent(true)
        setResendCooldown(60)
      }
    })
  }

  useEffect(() => {
    if (!newAccountEmail || !token) return
    const key = `@CANNECT_PROGRAM_PASSWORD_EMAIL:${token}`
    try {
      if (sessionStorage.getItem(key)) {
        setPasswordEmailSent(true)
        return
      }
      sessionStorage.setItem(key, '1')
    } catch {
      /* sem sessionStorage o e-mail pode sair de novo, o que é inofensivo */
    }
    sendPasswordEmail()
  }, [newAccountEmail, token])

  useEffect(() => {
    if (resendCooldown <= 0) return undefined
    const timer = window.setTimeout(() => setResendCooldown((current) => current - 1), 1000)
    return () => window.clearTimeout(timer)
  }, [resendCooldown])

  const confirmingPayment =
    Boolean(token) && hasPaidProgramPayment(token) && (next?.phase === 'offer' || next?.phase === 'awaiting_payment')

  useEffect(() => {
    if (!confirmingPayment || !token) return undefined

    const tick = async () => {
      try {
        const orderId = readProgramPaymentSummary()?.orderId
        if (orderId) {
          await attachProgramOrderIfNeeded({ id: orderId, is_program_order: true })
          return
        }
        const order = await postScreeningProgramOrder(token)
        if (order.already_paid) await queryClient.invalidateQueries({ queryKey: ['screening-next', token] })
      } catch {
        /* a próxima tentativa repete */
      }
    }

    tick()
    const timer = window.setInterval(tick, 4000)

    return () => window.clearInterval(timer)
  }, [confirmingPayment, token])

  useEffect(() => {
    if (!token) return
    // Sem o estado da triagem não dá para saber o passo certo; sincronizar antes empurrava o reload para o início.
    if (isLoading && !next) return
    if (routeStep === 'escolha' && !queryToken) return
    if ((routeStep === 'nome' || routeStep === 'whatsapp') && !queryToken) return
    if (
      (routeStep === 'identificacao' || routeStep === 'pagamento' || routeStep === 'pagamento-erro') &&
      (next?.phase === 'offer' || next?.phase === 'awaiting_payment')
    )
      return
    // Pago: mostra a confirmação antes de seguir para a agenda.
    if (routeStep === 'pago' && next?.phase === 'awaiting_scheduling') return
    // Pagamento recebido, mas o vínculo do pedido ainda não chegou (o postback da Pagar.me demora): fica na
    // confirmação, que tenta vincular sozinha, em vez de voltar para a oferta ou a identificação.
    if (confirmingPayment) {
      if (routeStep !== 'pago') {
        const params = new URLSearchParams(search)
        params.delete('cart_id')
        const qs = params.toString()
        history.replace(buildTrailPath('pago', token, qs ? `?${qs}` : ''))
      }
      return
    }
    // O vínculo do pedido pago leva a sessão a awaiting_scheduling enquanto a rota ainda é a do pagamento: vai direto
    // para a confirmação, sem passar pela agenda e voltar.
    if ((routeStep === 'pagamento' || routeStep === 'pagamento-erro') && next?.phase === 'awaiting_scheduling') {
      const params = new URLSearchParams(search)
      params.delete('cart_id')
      const qs = params.toString()
      history.replace(buildTrailPath('pago', token, qs ? `?${qs}` : ''))
      return
    }
    if (reviewOffer && routeStep === 'oferta') return
    const desired = overlay ? stepFromNext(next, overlay) : currentStep
    if (routeStep !== desired) {
      history.replace(buildTrailPath(desired, token, search))
    }
  }, [token, currentStep, overlay, routeStep, history, search, next, queryToken, reviewOffer, isLoading])

  // Ao voltar a uma pergunta de dados já respondida (ou abrir a sessão em outra aba) a API devolve a resposta em
  // next.value; sem isto o campo abria vazio e só nome/telefone se salvavam, por terem estado próprio.
  const storedCollectText = () => {
    const stored = next?.value
    if (typeof stored !== 'string' || !stored) return ''
    if (!['who', 'birthday', 'contact'].includes(next?.phase || '')) return ''
    if (next?.question?.type === 'date')
      return stored.includes('-') ? stored.slice(0, 10).split('-').reverse().join('/') : stored
    if (next?.question?.type === 'phone') return formatPhone(stored)
    return stored
  }

  useEffect(() => {
    setCollectValue(storedCollectText())
    setOptionPick('')
    setYnValue(null)
    setYnDetail('')
    setMultiPick(
      next?.phase === 'clinical' && next.question?.type === 'multi' && Array.isArray(next.value)
        ? next.value.map(String)
        : []
    )
    setError('')
    if (next?.question?.key === 'imc' && next.value) {
      const parsed = parseImcValue(next.value)
      if (parsed) {
        setHeight(String(parsed.altura || ''))
        setWeight(String(parsed.peso || ''))
        setMaxWeight(parsed.pesoMaximo ? String(parsed.pesoMaximo) : '')
        setMeta(parsed.meta ? String(parsed.meta) : '')
      }
    }
    if (next?.contact?.contact_email) setEmail(String(next.contact.contact_email))
    // O CPF só vem da própria sessão da triagem (depois do vínculo): o paciente pode não ser a conta logada.
    const knownCpf = next?.contact?.cpf
    if (knownCpf) setCpf(formatCpf(String(knownCpf)))
  }, [next?.question?.key, next?.phase])

  const busy =
    createSession.isPending ||
    contact.isPending ||
    answer.isPending ||
    back.isPending ||
    declineMutation.isPending ||
    bindCpf.isPending ||
    createProgramOrder.isPending ||
    createAppointment.isPending

  const go = (path: string) => history.push(path)

  const applyNext = (payload: TScreeningNext, snapshot?: ReturnType<typeof parseImcValue>) => {
    const imc = snapshot || imcSnapshotRef.current
    if (payload.phase === 'eligibility' && payload.question?.key === 'safety' && imc) {
      setImcSnapshot(imc)
      setOverlay('imc')
    }
    if (payload.phase === 'ineligible' && payload.exit_reason === 'imc' && imc) {
      setImcSnapshot(imc)
      setOverlay('imc')
    }
  }

  const handleChoice = () => {
    if (choice === 'pharmacy') {
      go(buildPharmacyCategoryPath('perda-de-peso'))
      return
    }
    setError('')
    setToken('')
    history.replace(buildTrailPath('nome', undefined, search))
  }

  // O aceite do termo acontece nesta tela: a API grava a versão aceita (e quando) na sessão.
  const withTerms = (payload: Record<string, unknown>) => ({ ...payload, terms_version: TERMS_VERSION })

  const submitStartContact = async () => {
    if (!consent || !startName.trim() || clearMask(startPhone).length < 10 || !isValidEmail(startEmail)) return
    setError('')
    try {
      let sessionToken = token
      if (!sessionToken) {
        const created = await createSession.mutateAsync({ heroSlug: PROGRAM_HERO_SLUG })
        if (!created.success || !created.data?.token) {
          setError('Não foi possível iniciar a triagem.')
          return
        }
        sessionToken = created.data.token
        persistScreeningToken(sessionToken)
        setToken(sessionToken)
      }

      let payload = await getScreeningNext(sessionToken)
      if (payload.question?.key === 'for_self') {
        payload = await postScreeningContact(sessionToken, withTerms({ for_self: true }))
      }
      if (payload.question?.key === 'patient_name') {
        payload = await postScreeningContact(sessionToken, withTerms({ patient_name: startName.trim() }))
      }
      if (payload.question?.key === 'contact_phone') {
        payload = await postScreeningContact(sessionToken, withTerms({ contact_phone: clearMask(startPhone) }))
      }
      if (payload.question?.key === 'contact_email') {
        payload = await postScreeningContact(sessionToken, withTerms({ contact_email: startEmail.trim() }))
      }

      queryClient.setQueryData(['screening-next', sessionToken], payload)
      applyNext(payload)
      history.replace(buildTrailPath(stepFromNext(payload), sessionToken, search))
    } catch (err) {
      setError(apiMessage(err, 'Não foi possível salvar seus dados.'))
    }
  }

  const submitContact = (payload: Record<string, unknown>) => {
    setError('')
    contact.mutate(payload, {
      onSuccess: (data) => applyNext(data),
      onError: (err) => setError(apiMessage(err, 'Não foi possível salvar.'))
    })
  }

  const submitAnswer = (payload: { key: string; value: unknown }) => {
    setError('')
    answer.mutate(payload, {
      onSuccess: (data) => applyNext(data),
      onError: (err) => setError(apiMessage(err, 'Não foi possível salvar.'))
    })
  }

  const handleBack = () => {
    if (routeStep === 'pagamento-erro') {
      history.replace(buildTrailPath('pagamento', token, search))
      return
    }
    if (routeStep === 'pagamento') {
      // O cart_id só serve ao pagamento; voltar ao passo anterior (dados do paciente) o descarta.
      const params = new URLSearchParams(search)
      params.delete('cart_id')
      const qs = params.toString()
      history.replace(buildTrailPath('identificacao', token, qs ? `?${qs}` : ''))
      return
    }
    if (routeStep === 'identificacao' && (next?.phase === 'offer' || next?.phase === 'awaiting_payment')) {
      setReviewOffer(true)
      history.replace(buildTrailPath('oferta', token, search))
      return
    }
    if (
      overlay === 'decline' ||
      overlay === 'imc' ||
      overlay === 'intro' ||
      overlay === 'confirm' ||
      overlay === 'scheduled'
    ) {
      if (overlay === 'imc') {
        setOverlay(null)
        back.mutate()
        return
      }
      setOverlay(null)
      return
    }
    if (!token || currentStep === 'escolha' || routeStep === 'nome' || routeStep === 'whatsapp') {
      if (routeStep === 'nome' || routeStep === 'whatsapp') {
        history.replace(buildTrailPath('escolha', undefined, search))
        return
      }
      onClose()
      return
    }
    setReviewOffer(false)
    back.mutate(undefined, {
      onError: () => onClose()
    })
  }

  const question: TScreeningQuestion | null = next?.question || null

  const handleCollect = () => {
    if (!question) return
    if (question.type === 'phone') {
      submitContact({ [question.key]: clearMask(collectValue) })
      return
    }
    if (question.type === 'date') {
      const digits = clearMask(collectValue)
      if (digits.length === 8) {
        setTypedBirthday(dateMask(collectValue))
        submitContact({
          [question.key]: `${digits.slice(4)}-${digits.slice(2, 4)}-${digits.slice(0, 2)}`
        })
        return
      }
    }
    submitContact({ [question.key]: collectValue.trim() })
  }

  const handleMeasures = () => {
    const parsed = {
      altura: Number(height.replace(',', '.')),
      peso: Number(weight.replace(',', '.')),
      meta: Number(meta.replace(',', '.')),
      ...(maxWeight.trim() ? { peso_maximo: Number(maxWeight.replace(',', '.')) } : {})
    }
    const meters = parsed.altura / 100
    const imc = Math.round((parsed.peso / (meters * meters)) * 10) / 10
    const snapshot = { altura: parsed.altura, peso: parsed.peso, meta: parsed.meta, imc }
    setImcSnapshot(snapshot)
    imcSnapshotRef.current = snapshot
    setError('')
    answer.mutate(
      { key: 'imc', value: { ...parsed, imc } },
      {
        onSuccess: (data) => applyNext(data, snapshot),
        onError: (err) => setError(apiMessage(err, 'Não foi possível salvar.'))
      }
    )
  }

  const toggleSafety = (id: string) => {
    const exclusive = id === 'none' || id === 'unknown'
    setSafety((current) => {
      if (current.includes(id)) return current.filter((item) => item !== id)
      if (exclusive) return [id]
      return [...current.filter((item) => item !== 'none' && item !== 'unknown'), id]
    })
  }

  const openProgramPayment = async () => {
    if (!token) return
    rememberTrailSearch(search)
    persistScreeningToken(token)
    // O pedido nasce da sessão da triagem (sem login) e é lido pelo uuid, igual ao link de carrinho.
    const order = await createProgramOrder.mutateAsync(token)
    if (order?.already_paid) {
      // O pedido desta sessão já foi pago: vai direto para a confirmação, sem abrir outro pagamento.
      await queryClient.invalidateQueries({ queryKey: ['screening-next', token] })
      history.replace(buildTrailPath('pago', token, search))
      return
    }
    if (!order?.uuid) {
      setError('Não foi possível preparar o pagamento do programa.')
      return
    }
    const params = new URLSearchParams(search)
    params.set('cart_id', order.uuid)
    history.replace(buildTrailPath('pagamento', token, `?${params.toString()}`))
  }

  const handleBindAndCart = async () => {
    if (!token) return
    setError('')
    try {
      if (email.trim()) {
        try {
          await contact.mutateAsync({ contact_email: email.trim() })
        } catch {
          /* e-mail pode já ter sido coletado */
        }
      }
      const bound = await bindCpf.mutateAsync({ token, cpf: clearMask(cpf) })
      // Conta nova já volta com sessão; conta existente segue só com o vínculo da triagem.
      if (bound?.token) {
        setTokenApi(bound.token)
        defaultsHeadersAxios(bound.token)
        await setUser({
          id: bound.user_id,
          name: bound.contact?.patient_name,
          email: bound.contact?.contact_email || email,
          cpf: clearMask(cpf)
        } as never)
      }
      // Traz a fase real (awaiting_payment) antes de abrir o pagamento: a de antes do vínculo ficava em cache.
      queryClient.setQueryData(['screening-next', token], await getScreeningNext(token))
      await openProgramPayment()
    } catch (err) {
      setError(apiMessage(err, 'Não foi possível seguir para o pagamento.'))
    }
  }

  const confirmBase: TConfirmData = {
    name: next?.contact?.patient_name || signedUser?.name || '',
    birthday: next?.contact?.birthday?.includes('-')
      ? next.contact.birthday.split('-').reverse().join('/')
      : next?.contact?.birthday || typedBirthday || '',
    cpf: formatCpf(next?.contact?.cpf || ''),
    email: next?.contact?.contact_email || signedUser?.email || ''
  }
  const confirmValues: TConfirmData = { ...confirmBase, ...confirmEdits }
  const confirmErrors: Partial<Record<keyof TConfirmData, string>> = {
    name: confirmValues.name.trim() ? undefined : 'Informe o nome completo',
    birthday: isValidBirthdayMask(confirmValues.birthday) ? undefined : 'Informe uma data de nascimento válida',
    cpf: isValidCpf(confirmValues.cpf) ? undefined : CPF_ERROR,
    email: isValidEmail(confirmValues.email) ? undefined : EMAIL_ERROR
  }
  const confirmValid = !Object.values(confirmErrors).some(Boolean)
  const confirmBirthdayIso = confirmValues.birthday.split('/').reverse().join('-')

  const handleScheduleConfirm = () => {
    if (!slot) return
    createAppointment.mutate(
      {
        origin: 'portal',
        // Sem patient_id: o paciente é a conta da sessão da triagem (o token prova), não a conta logada no navegador.
        prescriber_id: slot.prescriberId,
        date: slot.date,
        hour: slot.hour,
        duration: slot.duration,
        prescriber_user_id: slot.prescriberUserId,
        origin_scheduling_id: 1,
        screening_token: token,
        patient_name: confirmValues.name.trim(),
        patient_birthday: confirmBirthdayIso,
        patient_cpf: clearMask(confirmValues.cpf),
        patient_email: confirmValues.email.trim()
      },
      {
        onSuccess: () => {
          persistScheduledLabel(slot.label)
          queryClient.invalidateQueries({ queryKey: ['screening-next', token] })
          setOverlay('scheduled')
        },
        onError: (err) => setError(apiMessage(err, 'Não foi possível agendar a consulta inclusa.'))
      }
    )
  }

  const toggleDecline = (id: string) =>
    setDecline((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]))

  const toggleMulti = (id: string) =>
    setMultiPick((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]))

  const handleClinical = () => {
    if (!question) return
    if (question.type === 'multi') {
      submitAnswer({ key: question.key, value: multiPick })
      return
    }
    if (question.type === 'text') {
      submitAnswer({ key: question.key, value: collectValue.trim() })
      return
    }
    if (question.type === 'yn') {
      const needsDetail =
        Array.isArray(question.options) &&
        (question.options as { freeText?: boolean; v: unknown }[]).some((item) => item.freeText && Number(item.v) === 1)
      if (ynValue == null) return
      submitAnswer({
        key: question.key,
        value: needsDetail && ynValue === 1 ? { v: 1, detail: ynDetail.trim() } : ynValue
      })
      return
    }
    if (question.type === 'opts') {
      submitAnswer({
        key: question.key,
        value: optionPick === '' ? undefined : Number.isNaN(Number(optionPick)) ? optionPick : Number(optionPick)
      })
    }
  }

  const showChoice = routeStep === 'escolha' || (!routeStep && !token)
  const startKey = next?.question?.key
  const showStartContact =
    !showChoice &&
    (startKey === 'patient_name' ||
      startKey === 'contact_phone' ||
      startKey === 'contact_email' ||
      startKey === 'for_self' ||
      ((!token || !next) && (routeStep === 'nome' || routeStep === 'whatsapp')))

  let body = null
  let footer = null
  let surface = false
  let showHelp = false
  let canGoBack = true
  let hideHeader = false
  let wide = false
  let actionsInBody = false
  const step = showChoice ? 'escolha' : showStartContact ? 'nome' : currentStep
  // Identificação e pagamento são telas da rota enquanto a fase da API ainda é `offer`; o stepper do desktop segue a rota.
  const routeOnlyStep =
    !overlay &&
    (routeStep === 'identificacao' ||
      routeStep === 'pagamento' ||
      routeStep === 'pagamento-erro' ||
      routeStep === 'pago')
      ? routeStep
      : step
  const isUnderage = next?.phase === 'ineligible' && next.exit_reason === 'underage' && !overlay
  const birthdayLabel = next?.contact?.birthday?.includes('-')
    ? next.contact.birthday.split('-').reverse().join('/')
    : next?.contact?.birthday || typedBirthday
  const clinical = question?.position || { index: 0, total: 0 }

  if (showChoice) {
    body = <ChoiceScreen value={choice} onChange={setChoice} />
    footer = (
      <TrailCta loading={busy} disabled={busy} onClick={handleChoice}>
        Continuar
      </TrailCta>
    )
  } else if (showStartContact) {
    body = (
      <StartContactScreen
        name={startName}
        phone={formatPhone(startPhone)}
        email={startEmail}
        consent={consent}
        onName={setStartName}
        onPhone={(value) => setStartPhone(formatPhone(value))}
        onEmail={setStartEmail}
        onConsent={setConsent}
        error={error}
      />
    )
    footer = (
      <TrailCta
        loading={busy}
        disabled={busy || !consent || !startName.trim() || !isValidPhone(startPhone) || !isValidEmail(startEmail)}
        onClick={() => void submitStartContact()}>
        Continuar
      </TrailCta>
    )
  } else if (confirmingPayment) {
    // Pagou e o vínculo do pedido ainda não chegou: nenhuma outra tela (oferta, identificação) pode aparecer.
    canGoBack = false
    hideHeader = true
    body = <PaymentConfirmingScreen />
  } else if (overlay === 'imc' && (imcSnapshot || imcSnapshotRef.current)) {
    const shown = imcSnapshot || imcSnapshotRef.current
    const eligible = next?.exit_reason !== 'imc'
    surface = true
    wide = eligible
    body = <ImcResultScreen imc={shown.imc} weight={shown.peso} goal={shown.meta} eligible={eligible} />
    footer = eligible ? <TrailCta onClick={() => setOverlay(null)}>Continuar</TrailCta> : <ExitFooter showConsulta />
  } else if (overlay === 'decline') {
    body = <DeclineScreen value={decline} onToggle={toggleDecline} />
    footer = (
      <>
        <TrailCta
          loading={busy}
          disabled={decline.length === 0}
          onClick={() =>
            declineMutation.mutate(
              { reasons: decline },
              {
                // Enviado o motivo, a pessoa sai da trilha.
                onSuccess: () => {
                  setOverlay(null)
                  onClose()
                },
                onError: (err) => setError(apiMessage(err, 'Não foi possível registrar.'))
              }
            )
          }>
          Enviar
        </TrailCta>
        <TrailGhost onClick={() => setOverlay(null)}>Voltar para o programa</TrailGhost>
      </>
    )
  } else if (overlay === 'scheduled' && slot) {
    canGoBack = false
    body = <ScheduledScreen slotLabel={scheduledPhrase(slot.label)} />
    footer = <TrailCta onClick={() => setOverlay('intro')}>Responder perguntas para o médico</TrailCta>
  } else if (overlay === 'intro' || (next?.phase === 'clinical' && !introSeen && !question)) {
    wide = true
    footer = (
      <TrailCta
        onClick={() => {
          setIntroSeen(true)
          setOverlay(null)
        }}>
        Começar
      </TrailCta>
    )
    body = <ClinicalIntroScreen />
  } else if (isUnderage) {
    canGoBack = false
    showHelp = true
    body = (
      <ExitScreen
        title="Esse programa não é para você"
        body="O Programa de Perda de Peso é indicado apenas para pessoas com 18 anos ou mais. Por isso, não conseguimos seguir com o seu cadastro."
        helpHref={WHATSAPP_PROGRAM_DOUBTS_URL}
        helpText="Fale com a nossa equipe pelo WhatsApp."
      />
    )
    footer = null
  } else if (next?.phase === 'ineligible' && next.exit_reason === 'imc' && (imcSnapshot || parseImcValue(next.value))) {
    const shown = imcSnapshot || parseImcValue(next.value)
    surface = true
    body = shown ? <ImcResultScreen imc={shown.imc} weight={shown.peso} goal={shown.meta} eligible={false} /> : null
    footer = <ExitFooter showConsulta />
  } else if (next?.phase === 'ineligible' && next.exit_reason === 'safety') {
    showHelp = true
    body = (
      <ExitScreen
        title="Você não tem perfil para o programa"
        body="Pelas suas respostas, o programa não é indicado neste momento. Se tiver dúvidas, fale com a nossa equipe."
        helpHref={WHATSAPP_PROGRAM_DOUBTS_URL}
        helpText="Fale com a nossa equipe pelo WhatsApp."
      />
    )
    footer = <ExitFooter />
  } else if (next?.phase === 'ineligible') {
    showHelp = true
    body = (
      <ExitScreen
        title="O programa não se encaixa agora"
        body="Pelas suas respostas, o programa completo não é o caminho indicado neste momento. Você pode falar com o atendimento ou seguir para uma consulta avulsa."
      />
    )
    footer = <ExitFooter showConsulta />
  } else if ((next?.phase === 'who' || next?.phase === 'birthday' || next?.phase === 'contact') && question) {
    const isPhone = question.type === 'phone'
    const isDate = question.type === 'date'
    const isEmail = question.type === 'email'
    const collectValid = isCollectValid(question.type, collectValue)
    let collectFieldError: string | undefined
    if (collectValue.trim() && !collectValid) {
      if (isPhone) collectFieldError = PHONE_ERROR
      else if (isEmail) collectFieldError = EMAIL_ERROR
      else if (isDate && clearMask(collectValue).length === 8)
        collectFieldError = 'Informe uma data de nascimento válida'
    }
    body = (
      <TextCollectScreen
        eyebrow={isDate ? 'Sobre você' : 'Seus dados'}
        title={question.text}
        helper={
          question.sub_text ||
          (question.key === 'patient_name'
            ? 'Nome e sobrenome como no documento'
            : isDate
              ? 'Essa informação ajuda o médico a avaliar o tratamento mais adequado para você.'
              : undefined)
        }
        label={
          isPhone ? 'WhatsApp' : isDate ? 'Data de nascimento' : question.type === 'email' ? 'E-mail' : 'Nome completo'
        }
        value={isPhone ? formatPhone(collectValue) : isDate ? dateMask(collectValue) : collectValue}
        onChange={(value) => setCollectValue(isPhone ? formatPhone(value) : isDate ? dateMask(value) : value)}
        type={isPhone ? 'tel' : question.type === 'email' ? 'email' : 'text'}
        placeholder={isPhone ? '(11) 99999-9999' : isDate ? 'DD/MM/AAAA' : undefined}
        hint={isDate ? 'Exemplo: 14/03/1988' : undefined}
        fieldError={collectFieldError}
      />
    )
    footer = (
      <TrailCta loading={busy} disabled={busy || !collectValid} onClick={handleCollect}>
        Continuar
      </TrailCta>
    )
  } else if (question?.key === 'imc') {
    body = (
      <MeasuresScreen
        height={height}
        weight={weight}
        maxWeight={maxWeight}
        meta={meta}
        onChange={(field, value) => {
          if (field === 'height') setHeight(value)
          if (field === 'weight') setWeight(value)
          if (field === 'maxWeight') setMaxWeight(value)
          if (field === 'meta') setMeta(value)
        }}
      />
    )
    footer = (
      <TrailCta loading={busy} disabled={busy || !height || !weight || !maxWeight || !meta} onClick={handleMeasures}>
        Ver meu IMC
      </TrailCta>
    )
  } else if (question?.key === 'safety') {
    const options = (Array.isArray(question.options) ? question.options : []) as {
      id?: string
      v: unknown
      l: string
    }[]
    body = <SafetyScreen options={options} selected={safety} onToggle={toggleSafety} />
    footer = (
      <TrailCta
        loading={busy}
        disabled={busy || safety.length === 0}
        onClick={() => submitAnswer({ key: 'safety', value: safety })}>
        Continuar
      </TrailCta>
    )
  } else if (reviewOffer && routeStep === 'oferta') {
    surface = true
    showHelp = true
    wide = true
    actionsInBody = true
    footer = (
      <OfferFooter
        disabled={busy}
        onAccept={() => {
          setReviewOffer(false)
          history.replace(buildTrailPath('identificacao', token, search))
        }}
        onDecline={() => setOverlay('decline')}
      />
    )
    body = <OfferScreen actions={footer} />
  } else if (routeStep === 'pago' && next?.phase === 'awaiting_scheduling') {
    canGoBack = false
    hideHeader = true
    body = <PaymentConfirmedScreen summary={readProgramPaymentSummary()} />
    footer = (
      <TrailCta onClick={() => history.replace(buildTrailPath('agenda', token, search))}>
        Agendar consulta médica
      </TrailCta>
    )
  } else if (routeStep === 'pagamento-erro' && token) {
    showHelp = true
    body = <PaymentFailedScreen summary={readProgramPaymentSummary()} />
    footer = (
      <>
        <TrailCta onClick={() => history.replace(buildTrailPath('pagamento', token, search))}>
          Tentar novamente
        </TrailCta>
        <TrailGhost
          onClick={() => {
            setPayMethod(payMethod === 'pix' ? 'credit' : 'pix')
            history.replace(buildTrailPath('pagamento', token, search))
          }}>
          Escolher outra forma de pagamento
        </TrailGhost>
      </>
    )
  } else if (routeStep === 'pagamento' && token) {
    // Carrinho embutido (Resumo, Endereço, Pagamento, Finalização): tem navegação e botões próprios.
    surface = true
    hideHeader = true
    wide = true
    body = (
      <ProgramCartFlow
        patient={{
          fullName: next?.contact?.patient_name || startName || signedUser?.name,
          cpf: clearMask(cpf) || clearMask(next?.contact?.cpf || ''),
          email: next?.contact?.contact_email || email || signedUser?.email,
          phone: clearMask(next?.contact?.contact_phone || ''),
          birth: next?.contact?.birthday?.includes('-') ? next.contact.birthday : undefined
        }}
        onBack={() => {
          // O cart_id só serve ao pagamento; voltar à identificação o descarta.
          const params = new URLSearchParams(search)
          params.delete('cart_id')
          const qs = params.toString()
          history.replace(buildTrailPath('identificacao', token, qs ? `?${qs}` : ''))
        }}
        onFailed={(summary) => {
          saveProgramPaymentSummary({ ...summary, paid: false })
          history.replace(buildTrailPath('pagamento-erro', token, search))
        }}
        onPaid={(summary) => {
          // Mantém o resumo gravado no pagamento (traz as parcelas) quando é do mesmo pedido.
          const saved = readProgramPaymentSummary()
          if (!saved || saved.orderId !== summary.orderId) saveProgramPaymentSummary(summary)
          const params = new URLSearchParams(search)
          params.delete('cart_id')
          const qs = params.toString()
          history.replace(buildTrailPath('pago', token, qs ? `?${qs}` : ''))
        }}
      />
    )
  } else if (routeStep === 'identificacao' || next?.phase === 'awaiting_payment') {
    surface = false
    showHelp = true
    body = (
      <BindScreen
        name={next?.contact?.patient_name || startName || signedUser?.name}
        birthday={birthdayLabel}
        cpf={formatCpf(cpf)}
        email={email}
        emailLocked={Boolean(next?.contact?.contact_email)}
        onCpf={(value) => setCpf(formatCpf(value))}
        onEmail={setEmail}
        error={error}
      />
    )
    footer = (
      <TrailCta
        loading={busy}
        disabled={busy || !isValidCpf(cpf) || !isValidEmail(email)}
        onClick={() => void handleBindAndCart()}>
        Continuar para o pagamento
      </TrailCta>
    )
  } else if (next?.phase === 'offer') {
    surface = true
    showHelp = true
    wide = true
    actionsInBody = true
    footer = (
      <OfferFooter
        disabled={busy}
        onAccept={() => history.replace(buildTrailPath('identificacao', token, search))}
        onDecline={() => setOverlay('decline')}
      />
    )
    body = <OfferScreen actions={footer} />
  } else if (overlay === 'confirm' && slot) {
    showHelp = true
    body = (
      <ConfirmDataScreen
        prescriber={slot.name}
        slotLabel={slot.label}
        values={confirmValues}
        locked={{
          name: Boolean(confirmBase.name),
          birthday: Boolean(confirmBase.birthday),
          cpf: Boolean(confirmBase.cpf),
          email: Boolean(confirmBase.email)
        }}
        errors={
          {
            name: confirmEdits.name !== undefined ? confirmErrors.name : undefined,
            birthday: confirmEdits.birthday !== undefined ? confirmErrors.birthday : undefined,
            cpf: confirmEdits.cpf !== undefined ? confirmErrors.cpf : undefined,
            email: confirmEdits.email !== undefined ? confirmErrors.email : undefined
          } as Partial<Record<keyof TConfirmData, string>>
        }
        onChange={(field, value) =>
          setConfirmEdits((current) => ({
            ...current,
            [field]: maskConfirmField(field, value)
          }))
        }
        onChangeSlot={() => setOverlay(null)}
      />
    )
    footer = (
      <TrailCta loading={busy} disabled={busy || !confirmValid} onClick={handleScheduleConfirm}>
        Confirmar agendamento
      </TrailCta>
    )
  } else if (next?.phase === 'awaiting_scheduling') {
    // Já está pago: a API não tem resposta anterior a desfazer aqui (400 em /back) e o pagamento não se refaz.
    canGoBack = false
    showHelp = true
    wide = true
    body = (
      <ScheduleScreen
        programPrescriberId={next.program_prescriber_id}
        selectedKey={slot ? `${slot.prescriberId}-${slot.date}-${slot.hour}` : undefined}
        onSelect={(payload) => {
          setSlot(payload)
        }}
      />
    )
    footer = slot ? (
      <TrailCta onClick={() => setOverlay('confirm')}>Continuar</TrailCta>
    ) : (
      <TrailCta disabled>Continuar</TrailCta>
    )
  } else if (next?.phase === 'clinical' && question) {
    if (!introSeen) {
      wide = true
      footer = <TrailCta onClick={() => setIntroSeen(true)}>Começar</TrailCta>
      body = <ClinicalIntroScreen />
    } else if (question.type === 'opts' && Array.isArray(question.options)) {
      body = (
        <OptionsScreen
          title={question.text}
          sub={question.sub_text}
          eyebrow={CLINICAL_TOPICS[question.key]}
          index={clinical.index}
          total={clinical.total}
          options={question.options as { v: unknown; l: string }[]}
          selected={optionPick}
          onSelect={(value) => setOptionPick(String(value))}
        />
      )
      footer = (
        <TrailCta loading={busy} disabled={busy || optionPick === ''} onClick={handleClinical}>
          Continuar
        </TrailCta>
      )
    } else if (question.type === 'multi' && Array.isArray(question.options)) {
      body = (
        <ClinicalMultiScreen
          title={question.text}
          sub={question.sub_text}
          eyebrow={CLINICAL_TOPICS[question.key]}
          index={clinical.index}
          total={clinical.total}
          options={question.options as { id?: string; v: unknown; l: string; exclusive?: boolean }[]}
          selected={multiPick}
          max={question.max_select}
          onToggle={toggleMulti}
        />
      )
      footer = (
        <TrailCta loading={busy} disabled={busy || multiPick.length === 0} onClick={handleClinical}>
          {clinical.index > 0 && clinical.index === clinical.total ? 'Concluir avaliação' : 'Continuar'}
        </TrailCta>
      )
    } else if (question.type === 'yn') {
      body = (
        <YnDetailScreen
          title={question.text}
          sub={question.sub_text}
          eyebrow={CLINICAL_TOPICS[question.key]}
          index={clinical.index}
          total={clinical.total}
          value={ynValue}
          detail={ynDetail}
          onValue={setYnValue}
          onDetail={setYnDetail}
        />
      )
      footer = (
        <TrailCta
          loading={busy}
          disabled={busy || ynValue == null || (ynValue === 1 && !ynDetail.trim() && question.sub_text != null)}
          onClick={handleClinical}>
          Continuar
        </TrailCta>
      )
    } else {
      body = (
        <ClinicalTextScreen
          title={question.text}
          sub={question.sub_text}
          eyebrow={CLINICAL_TOPICS[question.key]}
          placeholder={CLINICAL_TEXT_PLACEHOLDERS[question.key]}
          index={clinical.index}
          total={clinical.total}
          value={collectValue}
          onChange={setCollectValue}
        />
      )
      footer = (
        <TrailCta loading={busy} disabled={busy || !collectValue.trim()} onClick={handleClinical}>
          Continuar
        </TrailCta>
      )
    }
  } else if (next?.phase === 'done') {
    const scheduledLabel = slot?.label || storedScheduledLabel()
    wide = Boolean(newAccountEmail)
    body = (
      <DoneScreen
        appointment={scheduledLabel ? scheduledAppointment(scheduledLabel) : undefined}
        newAccount={
          newAccountEmail
            ? {
                email: newAccountEmail,
                sent: passwordEmailSent,
                sending: passwordEmail.isPending,
                failed: passwordEmail.isError,
                cooldown: resendCooldown,
                onResend: sendPasswordEmail
              }
            : undefined
        }
      />
    )
    footer = (
      <DoneFooter
        newAccount={Boolean(newAccountEmail)}
        onConsultation={() => window.location.assign(newAccountEmail ? '/login' : MY_APPOINTMENTS_PATH)}
      />
    )
  } else if (next?.phase === 'declined') {
    body = (
      <ExitScreen
        title="Tudo bem, a gente espera"
        body="Quando quiser retomar o programa, é só voltar a esta página. O atendimento segue disponível se você tiver dúvidas."
      />
    )
    footer = <ExitFooter />
  } else if (isLoading) {
    body = <p className="text-sm text-[#6B6560]">Carregando…</p>
  } else if (isError) {
    body = (
      <p className="text-sm text-[#6B6560]">Não foi possível carregar esta triagem. Feche e tente começar de novo.</p>
    )
  }

  const inClinical = overlay === 'intro' || next?.phase === 'clinical'
  let progressOverride: number | undefined
  if (inClinical) {
    progressOverride =
      question && clinical.total > 0
        ? CLINICAL_PROGRESS_START + Math.round((clinical.index / clinical.total) * CLINICAL_PROGRESS_SPAN)
        : CLINICAL_PROGRESS_START
  }

  return (
    <TrailShell
      open
      onClose={onClose}
      stepIndex={stepIndexFor(step, overlay || undefined)}
      stageStepIndex={stepIndexFor(routeOnlyStep, overlay || undefined)}
      onBack={canGoBack ? handleBack : undefined}
      showHelp={showHelp}
      surface={surface}
      hideHeader={hideHeader}
      wide={wide}
      actionsInBody={actionsInBody}
      completed={next?.phase === 'done'}
      confirmClose={next?.phase !== 'ineligible'}
      progressOverride={progressOverride}
      scrollKey={`${routeStep}|${overlay}|${next?.phase}|${question?.key}|${reviewOffer}`}
      footer={
        footer ? (
          <>
            {footer}
            {error ? <p className="mt-2 text-center text-sm text-[#DC4E5A]">{error}</p> : null}
          </>
        ) : error ? (
          <p className="text-center text-sm text-[#DC4E5A]">{error}</p>
        ) : undefined
      }>
      {body}
    </TrailShell>
  )
}

export default WeightLossProgramTrail
